<script setup>
import { nextTick, onMounted, reactive, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { api, post, postStream } from "../api"
import ChatMessageContent from "../components/ChatMessageContent.vue"

const route = useRoute(), router = useRouter()
const chats = ref([]), selected = ref(null), draft = ref(""), loading = ref(true), sending = ref(false), error = ref("")
const messagesElement = ref(null)
let systemMessageSequence = 0

async function addSystemMessage(message) {
  if (!selected.value) { error.value = message; return }
  systemMessageSequence += 1
  selected.value.messages.push({ id: `system-error-${Date.now()}-${systemMessageSequence}`, role: "system", content: message, created_at: new Date().toISOString() })
  await scrollToBottom()
}

async function loadHistory() {
  chats.value = await api("/chats")
}

async function openChat(id) {
  if (!id) { selected.value = null; return }
  loading.value = true; error.value = ""
  try { selected.value = await api(`/chats/${id}`); await scrollToBottom() }
  catch (requestError) { error.value = requestError.message; selected.value = null }
  finally { loading.value = false }
}

async function createChat() {
  error.value = ""; loading.value = true
  try {
    const chat = await post("/chats", {})
    await loadHistory()
    await router.push(`/chats/${chat.id}`)
  } catch (requestError) { error.value = requestError.message }
  finally { loading.value = false }
}

async function sendMessage() {
  const content = draft.value.trim()
  if (!content || !selected.value || sending.value) return
  sending.value = true; error.value = ""; draft.value = ""
  const pendingId = `pending-${Date.now()}`
  const assistantMessage = reactive({ id: `${pendingId}-assistant`, role: "assistant", content: "", thinking: true, streaming: true, created_at: new Date().toISOString() })
  selected.value.messages.push({ id: pendingId, role: "user", content, created_at: new Date().toISOString() }, assistantMessage)
  await scrollToBottom()
  const chatId = selected.value.id
  try {
    await postStream(`/chats/${selected.value.id}/messages/stream`, { message: { content } }, async event => {
      if (event.type === "delta") {
        assistantMessage.thinking = false
        await revealDelta(assistantMessage, event.content)
      } else if (event.type === "error") {
        throw new Error(event.message)
      }
    })
    selected.value = await api(`/chats/${selected.value.id}`)
    await loadHistory(); await scrollToBottom()
  } catch (requestError) {
    await openChat(chatId)
    await addSystemMessage(requestError.message || "Не удалось получить ответ модели")
  } finally { sending.value = false }
}

async function revealDelta(message, text) {
  const characters = Array.from(text || "")
  const batchSize = characters.length > 48 ? 16 : characters.length
  for (let index = 0; index < characters.length; index += batchSize) {
    message.content += characters.slice(index, index + batchSize).join("")
    await scrollToBottom()
    await new Promise(resolve => requestAnimationFrame(resolve))
  }
}

async function scrollToBottom() {
  await nextTick()
  if (messagesElement.value) messagesElement.value.scrollTop = messagesElement.value.scrollHeight
}

function submitOnEnter(event) {
  if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); sendMessage() }
}

watch(() => route.params.id, id => openChat(id))
onMounted(async () => {
  try {
    await loadHistory()
    if (route.params.id) await openChat(route.params.id)
    else if (chats.value.length) await router.replace(`/chats/${chats.value[0].id}`)
  } catch (requestError) { error.value = requestError.message }
  finally { loading.value = false }
})
</script>

<template>
  <section class="page chat-page">
    <div class="page-heading"><div><small>RubyLLM · OpenRouter</small><h1>AI-чат</h1></div><button class="primary" @click="createChat">+ Новый чат</button></div>
    <div v-if="error && !selected" class="chat-error">{{ error }} <RouterLink v-if="error.includes('настро') || error.includes('модел')" to="/settings/ai">Открыть настройки</RouterLink></div>
    <div class="chat-layout panel">
      <aside class="chat-history"><header>История чатов</header><div v-if="!chats.length" class="chat-empty-history">История пока пуста</div><RouterLink v-for="chat in chats" :key="chat.id" :to="`/chats/${chat.id}`" :class="{active:String(route.params.id)===String(chat.id)}"><strong>{{ chat.title }}</strong><span>{{ chat.preview || chat.model }}</span><time>{{ new Date(chat.updated_at).toLocaleString('ru-RU') }}</time></RouterLink></aside>
      <main class="chat-conversation">
        <div v-if="loading" class="loading">Загрузка…</div>
        <div v-else-if="!selected" class="chat-welcome"><span>✦</span><h2>Начните новый чат</h2><p>После первого сообщения чат появится в истории.</p><button class="primary" @click="createChat">Создать чат</button></div>
        <template v-else>
          <header class="chat-title"><div><strong>{{ selected.title }}</strong><small>{{ selected.model }}</small></div></header>
          <div ref="messagesElement" class="chat-messages">
            <div v-if="!selected.messages.length" class="chat-welcome compact"><span>✦</span><h2>Чем помочь?</h2><p>Например: «Найди партнёра по имени, email или интеграции»</p></div>
            <article v-for="message in selected.messages" :key="message.id" class="chat-message" :class="[message.role,{typing:message.thinking,streaming:message.streaming}]"><span>{{ message.role === 'user' ? 'Вы' : message.role === 'system' ? '!' : 'AI' }}</span><div v-if="message.thinking && !message.content"><span class="thinking-loader"><i></i><i></i><i></i></span> Думает…</div><div v-else><strong v-if="message.role === 'system'" class="chat-system-label">Системное сообщение</strong><ChatMessageContent :content="message.content"/><span v-if="message.streaming" class="stream-cursor" aria-hidden="true"></span></div></article>
          </div>
          <form class="chat-composer" @submit.prevent="sendMessage"><textarea v-model="draft" rows="2" placeholder="Сообщение…" :disabled="sending" @keydown="submitOnEnter"></textarea><button class="primary" :disabled="sending || !draft.trim()">{{ sending ? "Отправка…" : "Отправить" }}</button><small>Enter — отправить, Shift+Enter — новая строка</small></form>
        </template>
      </main>
    </div>
  </section>
</template>
